import { useEffect, useState } from "react";
import api from "../../services/api";
import {
  listarDemandasActivas,
  extraerCriteriosDesdeDemanda,
  buildBuscarPropiedadesQuery,
} from "../../utils/demandaCriteriosBusqueda";

const COLS = [
  { id: "imagenes", label: "Imágenes", width: 120 },
  { id: "operaciones", label: "Op. activas", width: 150 },
  { id: "tipo", label: "Tipo", width: 110 },
  { id: "precio_venta", label: "Precio venta", width: 110 },
  { id: "localidad", label: "Localidad", width: 110 },
  { id: "area", label: "Zona", width: 110 },
  { id: "habitaciones", label: "Hab.", width: 60 },
  { id: "piscina", label: "Pisc.", width: 60 },
  { id: "enviada", label: "Enviada", width: 78 },
  { id: "visitada", label: "Visitada", width: 78 },
  { id: "agente", label: "Agente", width: 130 },
];

function fotoUrl(url) {
  if (!url) return null;
  const s = String(url);
  if (s.startsWith("http")) return s;
  return s.startsWith("/") ? s : `/${s}`;
}

function formatPrecio(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toLocaleString("es-ES")} €`;
}

function nombreCliente(c) {
  return `${c?.nombre || ""} ${c?.apellido || c?.apellidos || ""}`.trim() || "Cliente";
}

function formatFechaCorta(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "Europe/Madrid",
    });
  } catch {
    return "";
  }
}

function mapByPropiedadId(rows) {
  const map = {};
  for (const row of rows || []) {
    if (row?.propiedad_id) map[String(row.propiedad_id)] = row;
  }
  return map;
}

/** Visitas con descarte cualificado por el cliente. */
function propDescartadaEnVisitas(visitInfo) {
  if (!visitInfo) return false;
  let visitas = visitInfo.visitas;
  if (typeof visitas === "string") {
    try { visitas = JSON.parse(visitas); } catch { visitas = []; }
  }
  if (!Array.isArray(visitas)) visitas = [];
  if (visitas.some((v) => v?.descartada_por_cliente === true || v?.descartada_por_cliente === "true")) {
    return true;
  }
  return visitInfo.descartada_por_cliente === true || visitInfo.descartada_por_cliente === "true";
}

function BadgeSiNo({ si, title, color }) {
  if (!si) {
    return <span style={{ fontSize: 12, color: "#94a3b8" }}>—</span>;
  }
  return (
    <span
      title={title || ""}
      style={{
        display: "inline-block",
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: "#fff",
        background: color || "#16a34a",
        borderRadius: 6,
        padding: "3px 8px",
        whiteSpace: "nowrap",
      }}
    >
      Sí
    </span>
  );
}

async function cargarPropsDeDemanda(clienteId, dm, operacionFallback, enviadasMap, visitadasMap) {
  const criterios = extraerCriteriosDesdeDemanda(dm, operacionFallback);
  const q = buildBuscarPropiedadesQuery(criterios, clienteId);
  const [rProps, rEstado] = await Promise.all([
    api.get(`/leads/propiedades/buscar?${q.toString()}`),
    api.get(`/demandas/${dm.id}/propiedades-estado`).catch(() => ({ data: { data: [] } })),
  ]);
  const props = rProps.data?.data || [];
  const estados = rEstado.data?.data || [];
  const descartadas = new Set(
    estados
      .filter((e) => e.descartada === true || e.descartada === "true")
      .map((e) => String(e.propiedad_id))
  );
  const filtradas = props
    .filter((p) => {
      const pid = String(p.id);
      if (descartadas.has(pid)) return false;
      if (propDescartadaEnVisitas(visitadasMap[pid])) return false;
      return true;
    })
    .map((p) => {
      const pid = String(p.id);
      const env = enviadasMap[pid] || null;
      const vis = visitadasMap[pid] || null;
      return {
        ...p,
        _enviada: Boolean(env),
        _enviadaInfo: env,
        _visitada: Boolean(vis),
        _visitadaInfo: vis,
      };
    });
  return {
    demandaId: dm.id,
    demandaNombre: dm.nombre || "Demanda",
    propiedades: filtradas,
  };
}

async function cargarGrupo(cliente) {
  const id = cliente.id;
  const nombre = nombreCliente(cliente);
  try {
    const det = await api.get(`/clientes/${id}`);
    const data = det.data?.data || det.data || {};
    const activas = listarDemandasActivas(data.demandas || []);
    if (!activas.length) {
      return { clienteId: id, nombre, demandas: [], sinDemanda: true, error: null };
    }
    const [rEnv, rVis] = await Promise.all([
      api.get(`/clientes/${id}/propiedades-enviadas`).catch(() => ({ data: { data: [] } })),
      api.get(`/clientes/${id}/propiedades-visitadas`).catch(() => ({ data: { data: [] } })),
    ]);
    const enviadasMap = mapByPropiedadId(rEnv.data?.data);
    const visitadasMap = mapByPropiedadId(rVis.data?.data);
    const opFallback = cliente.tipo_operacion || data.tipo_operacion;
    const demandas = [];
    for (const dm of activas) {
      // Secuencial: una demanda tras otra
      // eslint-disable-next-line no-await-in-loop
      demandas.push(await cargarPropsDeDemanda(id, dm, opFallback, enviadasMap, visitadasMap));
    }
    const totalProps = demandas.reduce((a, d) => a + d.propiedades.length, 0);
    return {
      clienteId: id,
      nombre,
      demandas,
      totalProps,
      sinDemanda: false,
      error: null,
    };
  } catch (e) {
    return {
      clienteId: id,
      nombre,
      demandas: [],
      totalProps: 0,
      sinDemanda: false,
      error: e?.response?.data?.error || e.message || "Error al cargar",
    };
  }
}

function Celda({ p, colId, t, isDark, onFicha }) {
  switch (colId) {
    case "imagenes": {
      const src = fotoUrl(p.fotos?.[0]);
      return src ? (
        <img
          src={src}
          alt=""
          style={{ width: "100%", height: 72, objectFit: "cover", display: "block", borderRadius: 4 }}
        />
      ) : (
        <div
          style={{
            minHeight: 56,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: t.textMuted,
            background: isDark ? t.bgPanel : "#e2e8f0",
            fontSize: 11,
            borderRadius: 4,
          }}
        >
          —
        </div>
      );
    }
    case "operaciones":
      return (
        <div style={{ position: "relative", fontSize: 12, lineHeight: 1.35, color: t.textSecondary }}>
          <button
            type="button"
            title="Ficha resumen"
            onClick={(e) => {
              e.stopPropagation();
              onFicha?.(p.id);
            }}
            style={{
              position: "absolute",
              top: -2,
              right: 0,
              border: "none",
              background: "transparent",
              color: t.textMuted,
              cursor: "pointer",
              padding: 2,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <line x1="3" y1="9" x2="21" y2="9" />
              <line x1="9" y1="3" x2="9" y2="21" />
            </svg>
          </button>
          <div>
            <span style={{ color: p.operacion === "Venta" ? t.accentGreen : t.accentRed }}>
              {p.operacion === "Venta" ? "✓" : "✗"}
            </span>
            {" "}Venta{" "}
            {p.operacion === "Venta" && (
              <span style={{ fontFamily: "monospace", color: isDark ? "#fbbf24" : "#2563eb" }}>
                {p.referencia}
              </span>
            )}
          </div>
          <div>
            <span style={{ color: p.operacion === "Alquiler" ? t.accentGreen : t.accentRed }}>
              {p.operacion === "Alquiler" ? "✓" : "✗"}
            </span>
            {" "}Alquiler{" "}
            {p.operacion === "Alquiler" && (
              <span style={{ fontFamily: "monospace", color: t.accentBlue }}>{p.referencia}</span>
            )}
          </div>
          <div>
            <span style={{ color: p.operacion === "Alquiler vacacional" ? t.accentGreen : t.accentRed }}>
              {p.operacion === "Alquiler vacacional" ? "✓" : "✗"}
            </span>
            {" "}Vacacional
          </div>
        </div>
      );
    case "tipo":
      return (
        <span style={{ fontSize: 13, color: t.textPrimary }}>
          {p.tipo_nombre || p.tipo || <span style={{ color: t.textMuted, fontStyle: "italic" }}>Sin tipo</span>}
        </span>
      );
    case "precio_venta":
      return (
        <span style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary }}>
          {formatPrecio(p.precio_venta != null ? p.precio_venta : p.precio)}
        </span>
      );
    case "localidad":
      return (
        <span style={{ fontSize: 13, color: t.textPrimary }}>
          {p.poblacion_nombre || p.poblacion || "—"}
        </span>
      );
    case "area":
      return (
        <span style={{ fontSize: 13, color: t.textPrimary }}>
          {p.zona_nombre || p.zona || "—"}
        </span>
      );
    case "habitaciones":
      return (
        <span style={{ fontSize: 13, color: t.textPrimary }}>
          {p.num_dormitorios ?? "—"}
        </span>
      );
    case "piscina": {
      const priv = !!p.eq_piscina_priv;
      const com = !!p.eq_piscina_comun;
      let txt = "—";
      if (priv && com) txt = "Priv / Com";
      else if (priv) txt = "Priv";
      else if (com) txt = "Com";
      return <span style={{ fontSize: 12, color: t.textPrimary }}>{txt}</span>;
    }
    case "enviada": {
      const info = p._enviadaInfo;
      let tip = "Enviada al cliente";
      if (info?.fecha_envio_whatsapp || info?.fecha_envio_email) {
        const parts = [];
        if (info.fecha_envio_whatsapp) parts.push(`WA ${formatFechaCorta(info.fecha_envio_whatsapp)}`);
        if (info.fecha_envio_email) parts.push(`Email ${formatFechaCorta(info.fecha_envio_email)}`);
        tip = parts.join(" · ") || tip;
      } else if (Array.isArray(info?.envios) && info.envios[0]?.fecha) {
        tip = formatFechaCorta(info.envios[0].fecha) || tip;
      }
      const esEmail = info?.enviada_email && !info?.enviada_whatsapp;
      return (
        <BadgeSiNo
          si={p._enviada}
          title={tip}
          color={esEmail ? "#d97706" : "#16a34a"}
        />
      );
    }
    case "visitada": {
      const info = p._visitadaInfo;
      const f = info?.ultima_visita ? formatFechaCorta(info.ultima_visita) : "";
      return (
        <BadgeSiNo
          si={p._visitada}
          title={f ? `Visita (${f})` : "Visita realizada"}
          color="#2563eb"
        />
      );
    }
    case "agente":
      return (
        <span style={{ fontSize: 13, color: t.textPrimary }}>
          {p.agente_nombre || <span style={{ color: t.textMuted, fontStyle: "italic" }}>Sin agente</span>}
        </span>
      );
    default:
      return null;
  }
}

/** stickyTop: px bajo cabeceras sticky del scroll padre (cliente + demanda). */
function TablaProps({ propiedades, t, isDark, onFicha, stickyTop = 0 }) {
  const thBg = isDark ? (t.bgPanel || "#0f172a") : "#f1f5f9";
  const thSt = {
    padding: "8px 10px",
    textAlign: "left",
    fontSize: 11,
    fontWeight: 700,
    color: t.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    borderBottom: `1px solid ${t.border}`,
    background: thBg,
    whiteSpace: "nowrap",
    position: "sticky",
    top: stickyTop,
    zIndex: 2,
    boxShadow: "0 1px 0 rgba(0,0,0,0.06)",
  };
  const tdSt = {
    padding: "6px 10px",
    borderBottom: `1px solid ${t.border}`,
    verticalAlign: "middle",
    fontSize: 13,
  };

  if (!propiedades.length) {
    return (
      <div style={{ padding: "18px 12px", color: t.textMuted, fontSize: 13 }}>
        No hay propiedades que se ajusten a los criterios.
      </div>
    );
  }

  // Sin overflowX propio: el scroll vertical del modal es el contenedor sticky.
  return (
    <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 1040 }}>
      <thead>
        <tr>
          {COLS.map((c) => (
            <th key={c.id} style={{ ...thSt, minWidth: c.width, width: c.width }}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {propiedades.map((p) => (
          <tr
            key={p.id}
            onClick={() => onFicha?.(p.id)}
            style={{ background: t.bgCard, cursor: "pointer" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = isDark ? (t.bgCardHover || "#1e293b") : "#eff6ff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = t.bgCard;
            }}
          >
            {COLS.map((c) => (
              <td key={c.id} style={{ ...tdSt, minWidth: c.width, width: c.width }}>
                <Celda p={p} colId={c.id} t={t} isDark={isDark} onFicha={onFicha} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Modal: propiedades que se ajustan a la demanda de uno o varios clientes.
 * Al cerrar se permanece en Contactos.
 */
export default function ModalPropiedadesQueSeAjustan({
  open,
  onClose,
  clientes = [],
  t,
  isDark,
  onAbrirFicha,
}) {
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(false);

  const idsKey = clientes.map((c) => c.id).join(",");

  useEffect(() => {
    if (!open || !clientes.length) {
      setGrupos([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const snapshot = [...clientes];
    (async () => {
      const results = [];
      for (const c of snapshot) {
        if (cancelled) break;
        // Secuencial para no saturar /leads/propiedades/buscar
        // eslint-disable-next-line no-await-in-loop
        results.push(await cargarGrupo(c));
      }
      if (!cancelled) {
        setGrupos(results);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // idsKey estabiliza la dependencia (clientes es array nuevo cada render)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idsKey]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 720,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div
        role="dialog"
        aria-labelledby="modal-props-ajustan-titulo"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(1200px, 95vw)",
          maxHeight: "90vh",
          background: t.bgCard,
          borderRadius: 14,
          border: `1px solid ${t.border}`,
          boxShadow: "0 20px 50px rgba(0,0,0,0.4)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "14px 18px",
            borderBottom: `1px solid ${t.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexShrink: 0,
          }}
        >
          <div>
            <div id="modal-props-ajustan-titulo" style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary }}>
              Propiedades que se ajustan
            </div>
            <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2 }}>
              {clientes.length} cliente{clientes.length !== 1 ? "s" : ""} · según demanda activa
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: `1px solid ${t.border}`,
              background: isDark ? "#374151" : "#f3f4f6",
              color: t.textPrimary,
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 13,
              fontFamily: "inherit",
            }}
          >
            Cerrar
          </button>
        </div>

        {/* Sin padding-top: si no, al hacer sticky queda una franja donde se ven las filas. */}
        <div style={{ flex: 1, overflow: "auto", padding: "0 16px 20px" }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: t.textMuted, fontSize: 14 }}>
              Buscando propiedades…
            </div>
          ) : (
            grupos.map((g) => (
              <div key={g.clienteId} style={{ marginBottom: 22 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 10,
                    flexWrap: "wrap",
                    position: "sticky",
                    top: 0,
                    zIndex: 5,
                    background: isDark ? "#181c24" : (t.bgCard || "#ffffff"),
                    marginLeft: -16,
                    marginRight: -16,
                    padding: "12px 16px 8px",
                  }}
                >
                  <span style={{ fontSize: 15, fontWeight: 700, color: t.textPrimary }}>{g.nombre}</span>
                  <span style={{ fontSize: 12, color: t.textMuted }}>
                    {g.sinDemanda
                      ? "Sin demanda activa"
                      : g.error
                        ? g.error
                        : `${g.demandas?.length || 0} demanda${(g.demandas?.length || 0) !== 1 ? "s" : ""} · ${g.totalProps || 0} propiedad${(g.totalProps || 0) !== 1 ? "es" : ""}`}
                  </span>
                </div>
                {g.sinDemanda ? (
                  <div style={{ padding: "14px 12px", color: t.textMuted, fontSize: 13 }}>
                    Este cliente no tiene demanda activa con criterios.
                  </div>
                ) : g.error ? (
                  <div style={{ padding: "14px 12px", color: t.accentRed || "#dc2626", fontSize: 13 }}>
                    {g.error}
                  </div>
                ) : (
                  (g.demandas || []).map((dm) => (
                    <div
                      key={dm.demandaId}
                      style={{
                        marginBottom: 16,
                        border: `1px solid ${t.border}`,
                        borderRadius: 10,
                      }}
                    >
                      <div
                        style={{
                          padding: "8px 12px",
                          background: isDark ? "#1e3a8a" : "#dbeafe",
                          borderBottom: `1px solid ${t.border}`,
                          display: "flex",
                          alignItems: "baseline",
                          gap: 8,
                          flexWrap: "wrap",
                          position: "sticky",
                          top: 40,
                          zIndex: 4,
                        }}
                      >
                        <span style={{ fontSize: 13, fontWeight: 700, color: isDark ? "#93c5fd" : "#1d4ed8" }}>
                          {dm.demandaNombre}
                        </span>
                        <span style={{ fontSize: 11, color: t.textMuted }}>
                          {dm.propiedades.length} propiedad{dm.propiedades.length !== 1 ? "es" : ""}
                        </span>
                      </div>
                      <TablaProps
                        propiedades={dm.propiedades}
                        t={t}
                        isDark={isDark}
                        onFicha={onAbrirFicha}
                        stickyTop={76}
                      />
                    </div>
                  ))
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
